import {HORIZONTAL_RULER, VERTICAL_RULER} from "./consts";
import {getCamera, getControls} from "../../../index";
import * as THREE from '../../../src/three.module.js';


let distance;
let currentDistance;
let normalDistance;
let verticalCtx;
let horizontalCtx;
let horizontalCanvas;
let verticalCanvas;
let rulerHeight;
let verticalConfig = {
    previousVerticalIndex: 0,
    generalStep: 0,
    betweenLessStep: 1,
    betweenMoreStep: 1,
    firstPoint: 0,
};
let horizontalConfig = {
    previousHorizontalIndex: 0,
    generalStep: 0,
    betweenLessStep: 1,
    betweenMoreStep: 1,
    firstPoint: 0,
};
let currentVerticalResult = [];
let currentHorizontalResult = [];
let resizeHorizontalCounter = {
    more: 0,
    less: 0
};
let resizeVerticalCounter = {
    more: 0,
    less: 0
};
let startHorizontalSize = {
    width: 1,
    height: 1
};
let startVerticalSize = {
    width: 1,
    height: 1
};
let aspect;
let currentAspect;
let isActiveRuler = false;
let vFOV;
let firstParameters = {
    width: 1,
    height: 1
};

export function createRuler(container) {
    let camera = getCamera();
    vFOV = THREE.MathUtils.degToRad( camera.fov );
    const controls = getControls();
    firstParameters = getFirstObjectParameters();
    normalDistance = controls.target.distanceTo(controls.object.position);
    currentDistance = controls.target.distanceTo(controls.object.position);
    distance = controls.target.distanceTo(controls.object.position);
    drawHorizontalRuler(container);
    drawVerticalRuler(container);
    aspect = controls.object.aspect;
    currentAspect = controls.object.aspect;
}

export function updateRulerSize(container, width, height) {
    drawHorizontalRuler(container, width - 55);
    drawVerticalRuler(container, height - 115);
    verticalCtx.transform(1, 0, 0, -1, 0, verticalCanvas.height);
}

export function updateDelimiters() {
    updateDelimitersSize(getControls());
}

export function setIsActiveRuler(value) {
    isActiveRuler = value;
}

export function getIsActiveRuler() {
    return isActiveRuler;
}

function updateDelimitersSize(controls) {
    distance = controls.target.distanceTo(controls.object.position);
    currentAspect = controls.object.aspect;
    resizeHorizontalRuler();
    resizeVerticalRuler();
    // change currentDistance for normal display ruler points
    if (distance < currentDistance) {
        resizeHorizontalCounter.less += 1;
        resizeVerticalCounter.less += 1;
    }
    if (distance > currentDistance) {
        resizeHorizontalCounter.more += 1;
        resizeVerticalCounter.more += 1;
    }
    currentDistance = distance;
}

function drawHorizontalRuler(container, width = null) {
    const rulerCanvas = getRulerCanvas(HORIZONTAL_RULER, container, width);
    horizontalCanvas = rulerCanvas;
    horizontalCtx = rulerCanvas.getContext('2d');
    setContextStyle(horizontalCtx);
    calculateHorizontalPoints();
}

function drawVerticalRuler(container, height = null) {
    const rulerCanvas = getRulerCanvas(VERTICAL_RULER, container, null, height);
    verticalCanvas = rulerCanvas;
    verticalCtx = rulerCanvas.getContext('2d');
    setContextStyle(verticalCtx);
    rulerHeight = height ? height : container.offsetHeight - 115;
    calculateVerticalPoints();
}

function resizeHorizontalRuler() {
    if (horizontalCtx) {
        horizontalCtx.clearRect(0, 0, horizontalCanvas.width, horizontalCanvas.height);
        calculateHorizontalPoints();
        horizontalCtx.stroke();
    }
}

function resizeVerticalRuler() {
    if (verticalCtx) {
        verticalCtx.clearRect(0, 0, verticalCanvas.width, verticalCanvas.height);
        calculateVerticalPoints();
        verticalCtx.stroke();
    }
}

function calculateHorizontalPoints() {
    let camera = getCamera();
    let verHeight = 2 * Math.tan( vFOV / 2 ) * getTargetDistanceToObject();
    let width = verHeight * camera.aspect;
    const zoomCoeff = width / firstParameters.width;
    for (let i = 0; i <= 7000; i += 0.5) {
        horizontalCtx.beginPath();
        if (i === 0) {
            horizontalCtx.moveTo(i + 15, 20);
            horizontalCtx.lineTo(i + 15, 30);
            horizontalCtx.lineTo(i + 450, 30);
            horizontalCtx.fillText(i.toString(), i + 15, 13);
        }
        const currentDelimiter = Math.ceil(i * zoomCoeff);
        calculateMoreValues(i, currentDelimiter);
        calculateLessValues(i, currentDelimiter);
        if (distance === currentDistance) {
            buildHorizontalRuler(currentDelimiter, i)
        }
        horizontalCtx.stroke();
    }
    horizontalConfig.betweenLessStep = 1;
    horizontalConfig.betweenMoreStep = 1;
    horizontalConfig.generalStep = 0;
    horizontalConfig.firstPoint = 0
}

function buildHorizontalRuler(currentDelimiter, i) {
    if (i % 18.5 === 0) {
        horizontalCtx.moveTo(i + 15, 20);
        horizontalCtx.lineTo(i + 15, 30);
        horizontalCtx.lineTo(i + 450, 30);
    }
    if (i % 37 === 0 && i % 5 === 0) {
        displayDelimiterPointsForHorizontalRuler(currentDelimiter, i)
    }
}

function displayDelimiterPointsForHorizontalRuler(currentDelimiter, i) {
    if (!isNaN(currentDelimiter)) {
        if (currentHorizontalResult.length > 1) {
            horizontalConfig.firstPoint = currentHorizontalResult[1];
        }
        if (horizontalConfig.firstPoint) {
            const nextHorizontalPoint = (Math.ceil((horizontalConfig.firstPoint) / 5) * 5) * horizontalConfig.generalStep;
            currentHorizontalResult.push(nextHorizontalPoint);
            horizontalConfig.generalStep++;
            drawHorizontalGeneralSteps(i, nextHorizontalPoint)
        } else {
            const displayPoint = Math.ceil((currentDelimiter) / 5) * 5;
            currentHorizontalResult[horizontalConfig.generalStep] = displayPoint;
            horizontalConfig.generalStep++;
            drawHorizontalGeneralSteps(i, displayPoint)
        }
    }
}

function drawHorizontalGeneralSteps(i, currentDelimiter) {
    horizontalCtx.moveTo(i + 15, 15);
    horizontalCtx.lineTo(i + 15, 30);
    horizontalCtx.lineTo(i + 450, 30);
    horizontalCtx.fillText(currentDelimiter.toString(), i + 15, 13);
}

function calculateMoreValues(i, currentDelimiter, currentPoint, zoomCoeff) {
    if (distance > currentDistance && i !== 0 && resizeHorizontalCounter.more !== 5) {
        if (i % 18.5 === 0) {
            const heightIntermediatePoint = resizeHorizontalCounter.more > 5 ? 17 : 20;
            horizontalCtx.moveTo(i + 15 + resizeHorizontalCounter.less - (resizeHorizontalCounter.more), heightIntermediatePoint);
            horizontalCtx.lineTo(i + 15 + resizeHorizontalCounter.less - (resizeHorizontalCounter.more), 30);
            horizontalCtx.lineTo(i + 450 + resizeHorizontalCounter.less - (resizeHorizontalCounter.more), 30);
        }
        if (i % 37 === 0 && i % 5 === 0) {
            horizontalCtx.moveTo(i + 15 + resizeHorizontalCounter.less - (resizeHorizontalCounter.more), 15);
            horizontalCtx.lineTo(i + 15 + resizeHorizontalCounter.less - (resizeHorizontalCounter.more), 30);
            horizontalCtx.lineTo(i + 450 + resizeHorizontalCounter.less - (resizeHorizontalCounter.more), 30);
            if (currentHorizontalResult.length > 0) {
                horizontalCtx.fillText((Math.ceil((currentHorizontalResult[horizontalConfig.betweenMoreStep]) / 5) * 5).toString(), i + 15 + resizeHorizontalCounter.less - (resizeHorizontalCounter.more), 13);
                horizontalConfig.betweenMoreStep++
            }
        }
    }
    updateMoreValues(i, currentDelimiter, currentPoint, zoomCoeff);
}

function updateMoreValues(i, currentDelimiter) {
    if (resizeHorizontalCounter.more === 5) {
        if (i === 0) {
            resetHorizontalRuler();
        }
        buildHorizontalRuler(currentDelimiter, i);
        if (i === 7000) {
            resizeHorizontalCounter.more = 0;
        }
    }
}

function calculateLessValues(i, currentDelimiter) {
    if (distance < currentDistance && i !== 0 && resizeHorizontalCounter.less !== 5) {
        if (i % 18.5 === 0) {
            const heightIntermediatePoint = resizeHorizontalCounter.less > 5 ? 17 : 20;
            horizontalCtx.moveTo(i + 15 - resizeHorizontalCounter.more + (resizeHorizontalCounter.less), heightIntermediatePoint);
            horizontalCtx.lineTo(i + 15 - resizeHorizontalCounter.more + (resizeHorizontalCounter.less), 30);
            horizontalCtx.lineTo(i + 450 - resizeHorizontalCounter.more + (resizeHorizontalCounter.less), 30);
        }
        if (i % 37 === 0 && i % 5 === 0) {
            horizontalCtx.moveTo(i + 15 - resizeHorizontalCounter.more + (resizeHorizontalCounter.less), 15);
            horizontalCtx.lineTo(i + 15 - resizeHorizontalCounter.more + (resizeHorizontalCounter.less), 30);
            horizontalCtx.lineTo(i + 450 - resizeHorizontalCounter.more + (resizeHorizontalCounter.less), 30);
            if (currentHorizontalResult.length > 0) {
                horizontalCtx.fillText((Math.ceil((currentHorizontalResult[horizontalConfig.betweenLessStep]) / 5) * 5).toString(), i + 15 - resizeHorizontalCounter.more + (resizeHorizontalCounter.less), 13);
                horizontalConfig.betweenLessStep++
            }
        }
    }
    if (resizeHorizontalCounter.less === 5) {
        if (i === 0) {
            resetHorizontalRuler();
        }
        buildHorizontalRuler(currentDelimiter, i);
        if (i === 7000) {
            resizeHorizontalCounter.less = 0;
            horizontalConfig.betweenLessStep = 1;
        }
    }
}

function calculateVerticalPoints() {
    let verHeight = 2 * Math.tan( vFOV / 2 ) * getTargetDistanceToObject();
    const zoomCoeff = verHeight / firstParameters.height;
    for (let i = 0; i <= 7000; i += 0.5) {
        let displayPoint = Math.ceil( i * zoomCoeff);
        if (i === 0) {
            verticalCtx.beginPath();
            verticalCtx.moveTo(15, i + 25);
            verticalCtx.lineTo(30, i + 25);
            verticalCtx.lineTo(30, i + 900);
            verticalCtx.save();
            verticalCtx.translate(15, i + 25);
            verticalCtx.rotate(-0.5 * Math.PI);
            verticalCtx.scale(-1, 1);
            verticalCtx.fillText((Math.ceil((displayPoint) / 5) * 5).toString(), 0, 0);
            verticalCtx.restore();
            verticalCtx.stroke();
        }
        calculateVerticalMoreValues(i, displayPoint, zoomCoeff);
        calculateVerticalLessValues(i, displayPoint, zoomCoeff);
        if (distance === currentDistance) {
            if (i === 0 && distance === currentDistance) {
                currentVerticalResult = [];
            }
            buildVerticalRuler(displayPoint, i);
            verticalCtx.restore();
        }
        verticalCtx.stroke();
    }
    verticalConfig.betweenLessStep = 1;
    verticalConfig.generalStep = 0;
    verticalConfig.firstPoint = 0
}

function calculateVerticalLessValues(i, displayPoint, zoomCoeff) {
    if (distance < currentDistance && i !== 0 && resizeVerticalCounter.less !== 5) {
        showBetweenPointsForVerticalRuler(i);
        if (i % 37 === 0 && i % 5 === 0) {
            showBetweenCriticalPoints(i);
            verticalCtx.stroke();
        }
    }
    recalculationOfCriticalPointsForVerticalRuler(displayPoint, i, zoomCoeff);
}

function calculateVerticalMoreValues(i, displayPoint) {
    if (distance > currentDistance && i !== 0 && resizeVerticalCounter.more !== 5) {
        showBetweenPointsForVerticalRuler(i);
        if (i % 37 === 0 && i % 5 === 0) {
            showBetweenCriticalPoints(i);
        }
    }
    recalculationOfCriticalPointsForVerticalRuler(displayPoint, i);
}

function showBetweenPointsForVerticalRuler(i) {
    if (i % 18.5 === 0) {
        verticalCtx.beginPath();
        verticalCtx.moveTo(20, i + 25 + resizeVerticalCounter.less - (resizeVerticalCounter.more));
        verticalCtx.lineTo(30, i + 25 + resizeVerticalCounter.less - (resizeVerticalCounter.more));
        verticalCtx.stroke();
    }
}

function showBetweenCriticalPoints(i) {
    verticalCtx.beginPath();
    verticalCtx.moveTo(15, i + 25 - resizeVerticalCounter.more + resizeVerticalCounter.less);
    verticalCtx.lineTo(30, i + 25 - resizeVerticalCounter.more + resizeVerticalCounter.less);
    verticalCtx.lineTo(30, i + 450 - resizeVerticalCounter.more + resizeVerticalCounter.less);
    verticalCtx.save();
    verticalCtx.translate(15, i + 25 - resizeVerticalCounter.more + resizeVerticalCounter.less);
    verticalCtx.rotate(-0.5 * Math.PI);
    verticalCtx.scale(-1, 1);
    if (currentVerticalResult.length > 0) {
        verticalCtx.fillText((Math.ceil((currentVerticalResult[verticalConfig.betweenLessStep]) / 5) * 5).toString(), 0, 0);
        verticalConfig.betweenLessStep++;
    }
    verticalCtx.restore();
}

/**
 * Build and show intermediate and critical points for vertical ruler
 * @param displayPoint
 * @param i
 */
function buildVerticalRuler(displayPoint, i) {
    if (i % 18.5 === 0) {
        verticalCtx.beginPath();
        verticalCtx.moveTo(20, i + 25);
        verticalCtx.lineTo(30, i + 25);
        verticalCtx.lineTo(30, i + 900);
        verticalCtx.stroke();
    }
    if (i % 37 === 0 && i % 5 === 0) {
        displayDelimiterPointsForVerticalRuler(displayPoint, i);
    }
}


/**
 * Build and show critical points in vertical ruler
 * @param displayPoint - point in ruler
 * @param i - counter
 */
function displayDelimiterPointsForVerticalRuler(displayPoint, i) {
    if (!isNaN(displayPoint)) {
        if (currentVerticalResult.length > 1) {
            verticalConfig.firstPoint = currentVerticalResult[1];
        }
        if (verticalConfig.firstPoint) {

            const nextVerticalPoint = (Math.ceil((verticalConfig.firstPoint) / 5) * 5) * verticalConfig.generalStep;
            currentVerticalResult.push(nextVerticalPoint);
            verticalConfig.generalStep++;
            verticalCtx.beginPath();
            verticalCtx.moveTo(15, i + 25);
            verticalCtx.lineTo(30, i + 25);
            verticalCtx.lineTo(30, i + 900);
            verticalCtx.save();
            verticalCtx.translate(15, i + 25);
            verticalCtx.rotate(-0.5 * Math.PI);
            verticalCtx.scale(-1, 1);
            if (nextVerticalPoint) {
                verticalCtx.fillText(nextVerticalPoint.toString(), 0, 0);
            }
        } else {
            currentVerticalResult[verticalConfig.generalStep] = displayPoint;
            verticalConfig.generalStep++;
            verticalCtx.beginPath();
            verticalCtx.moveTo(15, i + 25);
            verticalCtx.lineTo(30, i + 25);
            verticalCtx.lineTo(30, i + 900);
            verticalCtx.save();
            verticalCtx.translate(15, i + 25);
            verticalCtx.rotate(-0.5 * Math.PI);
            verticalCtx.scale(-1, 1);
            if (displayPoint) {
                verticalCtx.fillText((Math.ceil((displayPoint) / 5) * 5).toString(), 0, 0);
            }
        }

        verticalCtx.restore();
    }
}

/**
 * Recalculate of delimiters when reaching the max critical point
 * @param displayPoint
 * @param i
 * @param zoomCoeff
 */
function recalculationOfCriticalPointsForVerticalRuler(displayPoint, i, zoomCoeff) {
    if (resizeVerticalCounter.more === 5) {
        if (i === 0) {
            resetVerticalResults();
        }
        displayPoint = Math.floor(i * zoomCoeff);
        buildVerticalRuler(displayPoint, i);
        if (i === 7000) {
            resizeVerticalCounter.more = 0;
        }
    }
    if (resizeVerticalCounter.less === 5) {
        if (i === 0) {
            resetVerticalResults();
        }
        displayPoint = Math.floor(i * zoomCoeff);
        buildVerticalRuler(displayPoint, i);
        if (i === 7000) {
            resizeVerticalCounter.less = 0;
            verticalConfig.betweenLessStep = 1;
        }
    }
}

function resetVerticalResults() {
    currentVerticalResult = [];
    verticalConfig.generalStep = 0;
}

function resetHorizontalRuler() {
    currentHorizontalResult = [];
    horizontalConfig.generalStep = 0;
}

function getRulerCanvas(canvasId, container, width = null, height = null) {
    let canvas = document.getElementById(canvasId);
    if (canvas) {
        setCanvasStyle(canvas, canvasId, container, width, height);
        return canvas;
    }
    canvas = document.createElement('canvas');
    canvas.setAttribute('id', canvasId);
    setCanvasStyle(canvas, canvasId, container, width, height);
    if (canvasId === VERTICAL_RULER) {
        let context = canvas.getContext('2d');
        context.transform(1, 0, 0, -1, 0, canvas.height);
        startVerticalSize = {
            width: canvas.width,
            height: canvas.height
        };
    }
    if (canvasId === HORIZONTAL_RULER) {
        startHorizontalSize = {
            width: canvas.width,
            height: canvas.height
        };
    }
    container.appendChild(canvas);
    return canvas;
}

function setCanvasStyle(canvas, canvasId, container, width, height) {
    canvas.style.position = 'absolute';
    canvas.style.backgroundColor = 'rgba(255,255,255,0.2)';
    canvas.style.zIndex = '2';
    if (!isActiveRuler) {
        canvas.style.display = 'none';
    }
    if (canvasId === HORIZONTAL_RULER) {
        canvas.width = width ? width : container.offsetWidth - 55;
        canvas.height = 30;
        canvas.style.bottom = '50px';
    }
    if (canvasId === VERTICAL_RULER) {
        canvas.width = 30;
        canvas.style.right = '10px';
        canvas.style.top = '35px';
        canvas.height = height ? height : container.offsetHeight - 115;
    }
}

function setContextStyle(ctx) {
    ctx.lineWidth = 1;
    ctx.textAlign = 'center';
    ctx.font = '14px Roboto';
}

/**
 * Get current distance from the object to the camera
 * @returns {*}
 */
function getTargetDistanceToObject() {
    let controls = getControls();
    let camera = getCamera();
    return camera.position.distanceTo(controls.target);
}


/**
 * Find first object parameters
 * @returns {{width: number, height: number}}
 */
function getFirstObjectParameters() {
    let camera = getCamera();
    let verHeight = 2 * Math.tan( vFOV / 2 ) * getTargetDistanceToObject();
    let width = verHeight * camera.aspect;
    return {
        width: width,
        height: verHeight
    };
}
